import { ICellRendererParams, RowIndexChangedEvent } from '@amzn/gft-ag-grid-enterprise';
import { isNull } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { PageSequenceCellRendererProps } from 'types/Common.types';

const PageSequenceCellRenderer = <TData,>(props: PageSequenceCellRendererProps<TData> & ICellRendererParams<TData>) => {
  const { api, node, colDef, readonly, onUpdate, value: cellValue, isRowDragging = false } = props;

  const [value, setValue] = useState<number>(0);
  const [, setEventRegistered] = useState<boolean>(false);

  const triggerCellRefresh = useCallback(
    (event: RowIndexChangedEvent) => {
      // When rowIndex is null, this is an indicator that the row has been removed from the Grid.
      if (isNull(event.node.rowIndex)) return;

      console.debug('triggerCellRefresh', event);
      if (colDef) {
        const tgtColumn = colDef.colId || colDef.field;
        if (tgtColumn) {
          api.refreshCells({ rowNodes: [event.node], columns: [tgtColumn], force: true });
        }
      }
    },
    [api, colDef],
  );

  /**
   * Initializes value and registers events on the parent AGGRid Component which are needed by this cell renderer
   */
  useEffect(() => {
    setEventRegistered((alreadyRegistered) => {
      if (!alreadyRegistered) {
        // Handles initializing the value for a new row
        const expectedValue = (node.rowIndex || 0) + 1;
        if (onUpdate && !readonly && cellValue?.toString() !== expectedValue.toString()) {
          onUpdate(node, expectedValue.toString());
        }

        // Register events
        node.addEventListener('rowIndexChanged', triggerCellRefresh);
        return true;
      }
      return alreadyRegistered;
    });

    return () => {
      node.removeEventListener?.('rowIndexChanged', triggerCellRefresh);
      setEventRegistered(false);
    };
  }, [api, cellValue, node, onUpdate, readonly, triggerCellRefresh, value]);

  /**
   * Updates the tracked value when rowIndex changes
   */
  useEffect(() => {
    setValue((previousValue) => {
      // In readonly, we only get the value from the assigned field (from colDef).
      // When `node.displayed` is false, this indicates the row is being removed from the grid.
      if (readonly || !node.displayed) {
        if (cellValue) {
          return typeof cellValue === 'string' ? parseInt(cellValue, 10) : (cellValue as number);
        }
        return previousValue;
      }

      // Otherwise, we update the pageNumber based on the rowIndex in the AGGrid table
      const newValue = (node.rowIndex ?? 0) + 1;
      if (newValue !== previousValue) {
        return newValue;
      }
      return previousValue;
    });
  }, [cellValue, node, node.rowIndex, onUpdate, readonly]);

  useEffect(() => {
    if (!isRowDragging && !readonly && onUpdate && cellValue && value && cellValue.toString() !== value.toString()) {
      console.debug(`cellValue == \`${cellValue.toString()}\` !== \`${value.toString()}\` (${typeof value})`, node);
      onUpdate(node, value.toString());
    }
  }, [cellValue, isRowDragging, node, onUpdate, readonly, value]);

  return value;
};

export default PageSequenceCellRenderer;
